import React, { useState } from "react";
import Icon from "../../../components/AppIcon";
import Image from "../../../components/AppImage";
import Button from "../../../components/ui/Button";
import { Checkbox } from "../../../components/ui/Checkbox";
import { useTranslation } from "react-i18next";

const CampaignTable = ({
  campaigns,
  selectedCampaigns,
  onSelectionChange,
  onEdit,
  onPauseResume,
  onDuplicate,
  onDelete,
  sortConfig,
  onSort,
}) => {

  const {t} = useTranslation();

  const [hoveredMedia, setHoveredMedia] = useState(null);

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { color: "bg-success text-success-foreground", icon: "Play" },
      paused: { color: "bg-warning text-warning-foreground", icon: "Pause" },
      completed: {
        color: "bg-secondary text-secondary-foreground",
        icon: "CheckCircle",
      },
      draft: { color: "bg-muted text-muted-foreground", icon: "Edit" },
      scheduled: { color: "bg-accent text-accent-foreground", icon: "Clock" },
    };

    const config = statusConfig?.[status] || statusConfig?.draft;

    return (
      <span
        className={`inline-flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium ${config?.color}`}
      >
        <Icon name={config?.icon} size={12} />
        <span className="capitalize">{status}</span>
      </span>
    );
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })?.format(amount);
  };

  const formatNumber = (num) => {
    if (num >= 1000000) {
      return (num / 1000000)?.toFixed(1) + "M";
    } else if (num >= 1000) {
      return (num / 1000)?.toFixed(1) + "K";
    }
    return num?.toString();
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      onSelectionChange(campaigns?.map((campaign) => campaign?.id));
    } else {
      onSelectionChange([]);
    }
  };

  const handleSelectCampaign = (campaignId, checked) => {
    if (checked) {
      onSelectionChange([...selectedCampaigns, campaignId]);
    } else {
      onSelectionChange(selectedCampaigns?.filter((id) => id !== campaignId));
    }
  };

  const getSortIcon = (column) => {
    if (sortConfig?.key !== column) {
      return (
        <Icon name="ArrowUpDown" size={14} className="text-muted-foreground" />
      );
    }
    return sortConfig?.direction === "asc" ? (
      <Icon name="ArrowUp" size={14} className="text-primary" />
    ) : (
      <Icon name="ArrowDown" size={14} className="text-primary" />
    );
  };

  const isAllSelected =
    campaigns?.length > 0 && selectedCampaigns?.length === campaigns?.length;
  const isIndeterminate =
    selectedCampaigns?.length > 0 &&
    selectedCampaigns?.length < campaigns?.length;

  return (
    <div className="bg-card border border-border rounded-lg shadow-subtle overflow-hidden">
      {/* Desktop Table */}
      <div className="hidden lg:block overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/50 border-b border-border">
            <tr>
              <th className="px-4 py-3 text-center">
                <span>{t("image")}</span>
              </th>

              <th className="px-4 py-3 text-center">
                <span>{t("date")}</span>
              </th>

              <th className="px-4 py-3 text-center">{t("actions")}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {campaigns?.map((campaign) => (
              <tr
                key={campaign?.id}
                className="hover:bg-muted/30 transition-micro"
              >
                <td className="px-4 py-4">
                  <div className="flex items-center space-x-3 text-center">
                    <div
                      className="text-center relative w-12 h-12 rounded-lg overflow-hidden bg-muted cursor-pointer"
                      onMouseEnter={() => setHoveredMedia(campaign?.id)}
                      onMouseLeave={() => setHoveredMedia(null)}
                    >
                      <Image
                        src={campaign?.image}
                        alt={campaign?.name}
                        className="w-full h-full object-cover"
                      />
                      {hoveredMedia === campaign?.id && (
                        <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                          <Icon name="Eye" size={16} color="white" />
                        </div>
                      )}
                    </div>
                  </div>
                </td>

                <td className="px-4 py-4">
                  <div className="text-sm text-center">
                    <div className="text-foreground">
                      {formatDate(campaign?.startDate)}
                    </div>
                    <div className="text-muted-foreground">
                      to {formatDate(campaign?.endDate)}
                    </div>
                  </div>
                </td>

                <td className="px-4 py-4">
                  <div className="flex items-center justify-center space-x-1">
                    <button
                      variant="ghost"
                      size="icon"
                      onClick={() => onEdit(campaign)}
                      className="h-8 w-8"
                    >
                      <Icon name="Edit" size={14} />
                    </button>

                    <button
                      variant="ghost"
                      size="icon"
                      onClick={() => onDelete(campaign)}
                      className="h-8 w-8 text-destructive hover:text-destructive"
                    >
                      <Icon name="Trash2" size={14} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* Mobile Cards */}
      <div className="lg:hidden divide-y divide-border">
        {campaigns?.map((campaign) => (
          <div key={campaign?.id} className="p-4">
            <div className="flex items-start space-x-3">
              <Checkbox
                checked={selectedCampaigns?.includes(campaign?.id)}
                onChange={(e) =>
                  handleSelectCampaign(campaign?.id, e?.target?.checked)
                }
                className="mt-1"
              />

              <div className="flex-1 space-y-3">
                {/* Header */}
                <div className="flex items-start justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 rounded-lg overflow-hidden bg-muted">
                      <Image
                        src={campaign?.image}
                        alt={campaign?.image}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div>
                      <h3 className="text-sm font-medium text-foreground">
                        {campaign?.name}
                      </h3>
                      <p className="text-xs text-muted-foreground">
                        {campaign?.type}
                      </p>
                    </div>
                  </div>
                  {getStatusBadge(campaign?.status)}
                </div>

                {/* Details */}
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-muted-foreground">Budget</p>
                    <p className="text-foreground font-medium">
                      {formatCurrency(campaign?.budget)}
                    </p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Spent</p>
                    <p className="text-foreground">
                      {formatCurrency(campaign?.spent)}
                    </p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Impressions</p>
                    <p className="text-foreground">
                      {formatNumber(campaign?.impressions)}
                    </p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">CTR</p>
                    <p className="text-foreground">{campaign?.ctr}%</p>
                  </div>
                </div>

                {/* Period */}
                <div className="text-sm">
                  <p className="text-muted-foreground">Campaign Period</p>
                  <p className="text-foreground">
                    {formatDate(campaign?.startDate)} -{" "}
                    {formatDate(campaign?.endDate)}
                  </p>
                </div>

                {/* Actions */}
                <div className="flex items-center space-x-2 pt-2">
                  <button
                    variant="outline"
                    size="sm"
                    onClick={() => onEdit(campaign)}
                    iconName="Edit"
                    iconPosition="left"
                  >
                    {t("edit")}
                  </button>
                  <button
                    variant="outline"
                    size="sm"
                    onClick={() => onPauseResume(campaign)}
                    iconName={campaign?.status === "active" ? "Pause" : "Play"}
                    iconPosition="left"
                  >
                    {campaign?.status === "active" ? "Pause" : "Resume"}
                  </button>
                  <button
                    variant="ghost"
                    size="sm"
                    onClick={() => onDuplicate(campaign)}
                    iconName="Copy"
                  />
                  <button
                    variant="ghost"
                    size="sm"
                    onClick={() => onDelete(campaign)}
                    iconName="Trash2"
                    className="text-destructive hover:text-destructive"
                  />
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default CampaignTable;
